import { Clock } from "lucide-react";
import { useTranslate } from "ra-core";

import { StatusPill } from "../design/StatusPill";
import type { Quote, QuoteApprovalStatus } from "../types";

/** A row without the field (legacy / non-custom quote) counts as approved, like the SQL default. */
export const approvalOf = (
  quote: Pick<Quote, "approval_status">,
): QuoteApprovalStatus => quote.approval_status ?? "aprobada";

/** Share link, print / PDF and WhatsApp stay disabled while a custom plan is pending or rejected. */
export const isShareBlocked = (quote: Pick<Quote, "approval_status">) =>
  approvalOf(quote) !== "aprobada";

/**
 * Almikia batch EQ3 (TASK-115): "Pendiente de aprobación" / "Rechazada" badge of a custom plan
 * (icon + text, never colour alone; there is no yellow in the indicator grammar, so pending is
 * the neutral navy tone). Renders nothing for an approved quote.
 */
export const QuoteApprovalBadge = ({
  quote,
}: {
  quote: Pick<Quote, "approval_status">;
}) => {
  const translate = useTranslate();
  const status = approvalOf(quote);
  if (status === "aprobada") return null;
  if (status === "rechazada") {
    return (
      <StatusPill
        tone="not-ok"
        text={translate("crm.quoter.approval.rejected")}
      />
    );
  }
  return (
    <span
      data-testid="quote-pending-badge"
      className="inline-flex items-center gap-1.5 rounded-full bg-[#E6ECF5] px-2.5 py-1 text-[12.5px] font-medium text-[#1F3A68]"
    >
      <Clock className="size-4" aria-hidden="true" />
      {translate("crm.quoter.approval.pending")}
    </span>
  );
};

/** The explanatory Spanish message shown next to the disabled share / print / WhatsApp buttons. */
export const QuoteApprovalHelp = ({
  quote,
  id,
}: {
  quote: Pick<Quote, "approval_status" | "approval_comment">;
  id?: string;
}) => {
  const translate = useTranslate();
  const status = approvalOf(quote);
  if (status === "aprobada") return null;
  return (
    <p id={id} className="text-sm text-muted-foreground">
      {status === "rechazada"
        ? translate("crm.quoter.approval.rejected_help", {
            comment: quote.approval_comment ?? "",
          })
        : translate("crm.quoter.approval.pending_help")}
    </p>
  );
};
